/**
 * ============================================================================
 * СЕРВИС ПРАКТИЧЕСКИХ ЗАДАНИЙ
 * ============================================================================
 * Методы для работы с практическими заданиями
 * ============================================================================
 */

import api from './api'

export const practicalService = {
	/**
	 * Получить все практические задания модуля
	 * @param {number} moduleId - ID модуля
	 * @param {boolean} includeUnpublished - Включать ли неопубликованные задания (по умолчанию true для преподавателей)
	 */
	async getByModuleId(moduleId, includeUnpublished = true) {
		const response = await api.get(`/modules/${moduleId}/practical`, {
			params: { includeUnpublished },
		})
		return response.data
	},

	/**
	 * Получить практическое задание по ID
	 */
	async getById(id) {
		const response = await api.get(`/practical/${id}`)
		return response.data
	},

	/**
	 * Создать новое практическое задание
	 */
	async create(moduleId, practicalData) {
		const response = await api.post(
			`/modules/${moduleId}/practical`,
			practicalData
		)
		return response.data
	},

	/**
	 * Обновить практическое задание
	 */
	async update(id, practicalData) {
		const response = await api.put(`/practical/${id}`, practicalData)
		return response.data
	},

	/**
	 * Удалить практическое задание
	 */
	async delete(id) {
		const response = await api.delete(`/practical/${id}`)
		return response.data
	},

	/**
	 * Опубликовать практическое задание
	 */
	async publish(id) {
		const response = await api.post(`/practical/${id}/publish`)
		return response.data
	},

	/**
	 * Отправить решение практического задания
	 */
	async submit(practicalId, submissionData) {
		const formData = new FormData()
		formData.append('notes', submissionData.notes)

		if (submissionData.files) {
			submissionData.files.forEach(file => {
				formData.append('files', file)
			})
		}

		const response = await api.post(
			`/practical/${practicalId}/submit`,
			formData,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			}
		)
		return response.data
	},

	/**
	 * Получить отправленные решения студента
	 */
	async getMySubmissions(practicalId) {
		const response = await api.get(`/practical/${practicalId}/submissions/my`)
		return response.data
	},

	/**
	 * Получить все отправленные решения (для преподавателя)
	 */
	async getAllSubmissions(practicalId) {
		const response = await api.get(`/practical/${practicalId}/submissions`)
		return response.data
	},

	/**
	 * Проверить решение студента
	 */
	async reviewSubmission(practicalId, submissionId, reviewData) {
		const response = await api.post(
			`/practical/${practicalId}/submissions/${submissionId}/review`,
			reviewData
		)
		return response.data
	},

	/**
	 * Загрузить материалы для практического задания
	 * @param {number} practicalId - ID практического задания
	 * @param {File[]} files - Массив файлов для загрузки
	 */
	async uploadMaterials(practicalId, files) {
		const formData = new FormData()
		files.forEach(file => {
			formData.append('materials', file)
		})

		const response = await api.post(
			`/practical/${practicalId}/materials`,
			formData,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			}
		)
		return response.data
	},

	/**
	 * Mark practice task as completed
	 * @param {number} taskId - Practical task ID
	 * @param {string} notes - Optional student notes
	 */
	async markComplete(taskId, notes = '') {
		const response = await api.post(`/practical/${taskId}/complete`, { notes })
		return response.data
	},
	/**
	 * Удалить материал практического задания
	 * @param {number} practicalId - ID практического задания
	 * @param {number} materialId - ID материала
	 */
	async deleteMaterial(practicalId, materialId) {
		if (materialId === undefined || materialId === null) {
			throw new Error('materialId is required')
		}
		let normalizedMaterialId = String(materialId)
		try {
			normalizedMaterialId = decodeURIComponent(normalizedMaterialId)
		} catch (error) {
			// already decoded or partially malformed - отправим как есть после безопасного encode
		}
		normalizedMaterialId = encodeURIComponent(normalizedMaterialId)
		const response = await api.delete(
			`/practical/${practicalId}/materials/${normalizedMaterialId}`
		)
		return response.data
	},
}

export default practicalService
